import React from 'react'
import { motion } from 'framer-motion'
import { Users, TrendingUp, Phone } from 'lucide-react'
import { useT } from '@/context/TranslateCtx'

const plans = [
  { k: 'free', price: '0', icon: Users, highlight: false },
  { k: 'pro', price: 'Soon!!', icon: TrendingUp, highlight: true },
  { k: 'ent', price: 'Custom', icon: Phone, highlight: false },
]

const card = {
  hidden: { opacity: 0, y: 60, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 120, damping: 20 } },
}

export default function Pricing() {
  const { t } = useT()
  return (
    <section id="pricing" className="relative bg-white py-32">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -right-20 w-[28rem] h-[28rem] bg-secondary opacity-20 rounded-full blur-3xl animate-pulse" />
      </div>

      <motion.h2
        className="text-4xl md:text-5xl font-extrabold text-center text-primary mb-20"
        variants={card}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
      >
        {t('pricing.heading')}
      </motion.h2>

      <div className="container grid gap-12 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((p, idx) => (
          <motion.div
            key={p.k}
            className={`group flex flex-col rounded-3xl p-8 transition-transform ${
              p.highlight
                ? 'bg-gradient-to-br from-primary to-secondary text-white shadow-2xl'
                : 'bg-gray-50 shadow-lg hover:shadow-2xl'
            }`}
            variants={card}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            whileHover={{ scale: 1.03, y: -6 }}
          >
            <motion.div
              className={`w-16 h-16 mb-6 rounded-full flex items-center justify-center ${
                p.highlight ? 'bg-white text-primary' : 'bg-primary text-white'
              }`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20, delay: idx * 0.1 }}
            >
              <p.icon size={28} strokeWidth={2.5} />
            </motion.div>

            <h3 className={`text-xl md:text-2xl font-semibold mb-4 ${p.highlight ? '' : 'text-gray-800'}`}>
              {t(`pricing.${p.k}.title`)}
            </h3>

            <div className="text-5xl font-extrabold mb-6">
              {p.price !== 'Custom' ? (
                <>
                  ฿{p.price}
                  <span className={`text-lg font-medium ${p.highlight ? 'opacity-80' : 'text-gray-500'}`}>
                    {t('pricing.period')}
                  </span>
                </>
              ) : (
                'Custom'
              )}
            </div>

            <ul className="flex-grow space-y-4 mb-8">
              {[1, 2, 3, 4, 5]
                .map(n => t(`pricing.${p.k}.feature${n}`))
                .filter(txt => !txt.startsWith('pricing.'))
                .map((txt, i) => (
                  <li key={i} className={`flex items-start gap-3 ${p.highlight ? 'text-white/90' : 'text-gray-700'}`}>
                    <span className="mt-1 text-base">{p.highlight ? '✓' : '✔︎'}</span>
                    <span className="text-base md:text-lg">{txt}</span>
                  </li>
                ))}
            </ul>

            <a
              href="https://app.kuizy.ai/register"
              className={`mt-auto py-3 rounded-full text-center font-semibold text-lg md:text-xl transition-colors ${
                p.highlight ? 'bg-white text-primary hover:bg-white/90' : 'bg-primary text-white hover:bg-secondary'
              }`}
            >
              {p.highlight ? t('pricing.cta.pro') : `${t('pricing.cta.start')} ${t(`pricing.${p.k}.title`)}`}
            </a>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="mt-16 flex justify-center"
        variants={card}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="w-72 h-20 bg-gray-100 border-2 border-dashed border-gray-300 rounded-2xl flex items-center justify-center text-gray-500 text-base">
          Image Thumbnail (Payment & Integrations)
        </div>
      </motion.div>

      <div className="absolute bottom-0 left-0 w-full h-16 bg-gradient-to-t from-gray-100 to-transparent pointer-events-none" />
    </section>
  )
}
